import React, { useEffect, useState, useRef } from "react";
import { Box, Button, Container, Grid, IconButton, Typography,} from "@mui/material";
import { useTranslation } from "react-i18next";
import Pagination from "../../common/Pagination";
import Skeleton from "../../common/Skeleton";
import MUIDataTable from "mui-datatables";
import DownloadButton from "../../common/DownloadBtn";
import { OrderLevelDataType, OrderLevelDataWithPaginationType } from "../../../interfaces/businessReportInterface";
import { BUSINESS_REPORT, LIST_LIMIT, SHEET_NAME } from "../../../constants/constants";
import { useAppSelector } from "../../../redux/hooks";
import BusinessReportDataApi from "../services/businessReportService";
import Utility from "../../../utils/Utility";
import downloadDataToExcel from "../../../utils/ExcelDownloadUtility";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import SortHeader from "../../common/SortHeader";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";

const OrderReport = (props: any) => {

    const { showSnackbar,valueFrom,valueTo,activeViewBy } = props;
    const firstPage = 1;
    const utility = new Utility();
    const [currentPage, setCurrentPage] = useState(firstPage);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [skeleton, setSkeleton] = useState(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [colFilter,setColFilter] =useState<string[]>([]);
    const [direction,setDirection]=useState<string[]>([]);
    const [colName, setColName] = useState<string>("");
    const apiData: any[][] = [];
    const[t] = useTranslation();
    const [, forceUpdate] = useState(0);
    const perPageLimit = useAppSelector((state) => state.common.perPageLimit);
    const [limit, setLimit] = useState(!perPageLimit ? LIST_LIMIT.BULK_PRODUCT_LIST : perPageLimit );
    const [orderLevelData, setOrderLevelData]= useState<OrderLevelDataType[]>([]);
    const startDate = valueFrom ? utility.getFormattedDateTime(valueFrom) : null;
    const endDate = valueTo ? utility.getFormattedDateTime(valueTo) : null; 
    const [hideColumnArr,setHideColumnArr] =useState<any>([]);
    const [showDownloadOptions , setShowDownloadOptions]=useState<boolean>(false);
    const node = useRef<HTMLDivElement>(null);

    useEffect(() => {
        forceUpdate(1);
    });

    useEffect(() => {
        if (!startDate || !endDate || !activeViewBy) return;

        if (colFilter || direction || (startDate && endDate && activeViewBy)) {
            orderLevelDataApiCall(firstPage, limit);
        }
    }, [startDate, endDate, activeViewBy, colFilter, direction]);

    const handleSortChange = (key: string, newDir: string) => {
        const idx = colFilter.indexOf(key);
        if (idx === -1) {
            setColFilter([...colFilter, key].slice(-3));
            setDirection([...direction, newDir].slice(-3));
        } else {
            const newDirs = [...direction];
            newDirs[idx] = newDir;
            setDirection(newDirs);
        }
    };

    const columns = [
        {
            name: t("pr.date"),
            options: {
                filter: true,
                sort: false,
                viewColumns: false,
                customHeadLabelRender: (data:any) => {
                    const idx = colFilter.indexOf("date");
                    return (
                        <SortHeader
                            label={data.label}
                            columnName="date"
                            colFilter={idx !== -1 ? "date" : ""}
                            direction={idx !== -1 ? direction[idx] : ""}
                            onSort={handleSortChange}
                        />
                    );
                },       
                // setCellHeaderProps: () => ({ className: "busniess_date_col" }),
                setCellHeaderProps: () => ({ style:{ width: "350px" } }),
            },
        },
    
        {
            name: t("business_report.number_of_orders"),
            options: {
                filter: false,
                sort: false,
                display: hideColumnArr.includes("受注数") ? false : true,
                customHeadLabelRender: (data:any) => {
                    const idx = colFilter.indexOf("number_of_orders");
                    return (
                        <SortHeader
                            label={data.label}
                            columnName="number_of_orders"
                            colFilter={idx !== -1 ? "number_of_orders" : ""}
                            direction={idx !== -1 ? direction[idx] : ""}
                            onSort={handleSortChange}
                        />
                    );
                },   
            },
        },
        {
            name: t("business_report.order_sales_amount"),
            options: {
                filter: true,
                sort: false,
                display: hideColumnArr.includes("受注額") ? false : true,
                customHeadLabelRender: (data:any) => {
                    const idx = colFilter.indexOf("order_sales_amount");
                    return (
                        <SortHeader
                            label={data.label}
                            columnName="order_sales_amount"
                            colFilter={idx !== -1 ? "order_sales_amount" : ""}
                            direction={idx !== -1 ? direction[idx] : ""}
                            onSort={handleSortChange}
                        />
                    );
                },
            },
        },
        {
            name: t("business_report.average_order_value"),
            options: {
                filter: true,
                sort: false,
                display: hideColumnArr.includes("平均受注額") ? false : true,
                customHeadLabelRender: (data:any) => {
                    const idx = colFilter.indexOf("average_order_value");
                    return (
                        <SortHeader
                            label={data.label}
                            columnName="average_order_value"
                            colFilter={idx !== -1 ? "average_order_value" : ""}
                            direction={idx !== -1 ? direction[idx] : ""}
                            onSort={handleSortChange}
                        />
                    );
                }, 
            },
        },
      
        {
            name: t("business_report.number_of_item_sold"),
            options: {
                filter: true,
                sort: false,
                display: hideColumnArr.includes("商品実売点数") ? false : true,
                customHeadLabelRender: (data:any) => {
                    const idx = colFilter.indexOf("number_of_item_sold");
                    return (
                        <SortHeader
                            label={data.label}
                            columnName="number_of_item_sold"
                            colFilter={idx !== -1 ? "number_of_item_sold" : ""}
                            direction={idx !== -1 ? direction[idx] : ""}
                            onSort={handleSortChange}
                        />
                    );
                },
            },
        },
        {
            name: t("business_report.avg_no_item_sold"),
            options: {
                filter: true,
                sort: false,
                display: hideColumnArr.includes("平均商品実売点数") ? false : true,
                customHeadLabelRender: (data:any) => {
                    const idx = colFilter.indexOf("avg_no_item_sold");
                    return (
                        <SortHeader
                            label={data.label}
                            columnName="avg_no_item_sold"
                            colFilter={idx !== -1 ? "avg_no_item_sold" : ""}
                            direction={idx !== -1 ? direction[idx] : ""}
                            onSort={handleSortChange}
                        />
                    );
                },
            },
        },
       
    ];

    const buildSortString = (): string => {
        if (colFilter.length === 0) return "";
        const fieldMap: { [key: string]: string } = {
            date: "date",
            number_of_orders: "numberOfOrders",
            order_sales_amount: "orderSalesAmount",
            average_order_value: "averageOrderValue",
            number_of_item_sold: "numberOfItemsSold",
            avg_no_item_sold: "averageItemsSold",
        };
        return colFilter.map((key, idx) => `${fieldMap[key]}:${direction[idx]?.toUpperCase() || "ASC"}`).join(",");
    };

    const orderLevelDataApiCall = (currentPage: number, limit: number) => {
        setShowDownloadOptions(false);
        const payload = {};
        const isHitDummyURL = false;
        setSkeleton(true);
        const sortStr = buildSortString();
        new BusinessReportDataApi(payload, isHitDummyURL)
            .orderLevelData(currentPage,limit, activeViewBy,startDate,endDate, sortStr)
            .then((res:OrderLevelDataWithPaginationType) => {
                setOrderLevelData(res?.OrderLevelDataLogs);
                setLimit(limit);
                setCurrentPage(currentPage);
                setTotalCount(res?.pagination?.totalCount);
                setTotalPages(res?.pagination?.totalPages);
                setSkeleton(false);
            })
            .catch((err:any) => {
                showSnackbar(err?.message, false);
                setSkeleton(false);
            });
        apiTableData();
    };

    const businessReportColorValue = (amount: any) => {
        return (
            <Box className={`${amount < 0 ? "business_report_red" : "business_report_black"}`}>{new Utility().numberWithCommas(amount)}</Box>
        );
    };

    const apiTableData = () => {
        {
            orderLevelData?.map((item: OrderLevelDataType, index: number) => {
                apiData[index] = [];
                apiData[index].push(
                    // (currentPage - 1) * limit + index + 1,
                    item?.date,
                    item?.numberOfOrders,
                    businessReportColorValue(item?.orderSalesAmount),
                    businessReportColorValue(item?.averageOrderValue),
                    businessReportColorValue(item?.numberOfItemsSold),
                    businessReportColorValue(item?.averageItemsSold)
                );
            });
        }
    };
    apiTableData();

    const downloadOrderLevelData = (startDate:string | null, endDate:string | null, fileType:string) => {
        setShowDownloadOptions(false);
        showSnackbar(t("bulk_product_index.please_wait"), true);
        const payload = {};
        const isHitDummyURL = false;
        setLoading(true);
        const sortStr = buildSortString();
        new BusinessReportDataApi(payload, isHitDummyURL)
            .downloadOrderLevelListData(startDate,endDate,fileType,sortStr,activeViewBy)
            .then((res) => {
                if(typeof res == "string"){
                    showSnackbar(res, true);
                    setLoading(false);
                } else {
                    handleOnExport(res?.OrderLevelDataLogs);
                }
            })
            .catch((err: any) => {
                setLoading(false);
                setSkeleton(false);
                showSnackbar(err?.message, false);
            });    
    };
    
    const handleOnExport = (downloadArray?: any) => {
        const downloadData: Array<object> = [];
        const formattedStartDate = startDate ? startDate.replaceAll("/", "") : "";
        const formattedEndDate = endDate ? endDate.replaceAll("/", "") : "";
        downloadArray?.map((item: any) => {
            downloadData.push({
                日付:item?.date,
                受注数: item?.numberOfOrders,
                受注額: item?.orderSalesAmount,
                平均受注額:item?.averageOrderValue,
                商品実売点数:item?.numberOfItemsSold,
                平均商品実売点数:item?.averageItemsSold
            });
        });  
        const fileName = `売上データ_${formattedStartDate}-${formattedEndDate}.xlsx`; 
        const numFmt="text";
        const allignment = true;
        downloadDataToExcel(downloadData,fileName,SHEET_NAME.ORDER_DATA,numFmt,allignment,BUSINESS_REPORT.ROW_HEIGHT);
        setLoading(false);
    };

    useEffect(() => {
        const handleClickOutside = (event: { target: any; }) => {
            if (node.current && !node.current.contains(event.target)) {
                setShowDownloadOptions(false);
                   
            } 
        };              
        document.addEventListener("mousedown", handleClickOutside);       
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []); 

    return (

        <>
            <Box>
                <Container sx={{display:"flex" , justifyContent:"flex-end"}}>
                    <Box width={"250px"} ref={node}> 
                        {totalCount <= BUSINESS_REPORT.BUSINESS_REPORT_DOWNLOAD_LIMIT ? 
                            <DownloadButton loading={loading}  route={window.location.pathname} functionToExecute={()=>downloadOrderLevelData(startDate,endDate,"xlsx")} className="product_list_download" btnName= {t("bulk_product_index.batch_download")} disableCondition={skeleton || loading || orderLevelData?.length == 0 || !orderLevelData} />
                            : totalCount <= BUSINESS_REPORT.BUSINESS_REPORT_DOWNLOAD_LIMIT_CSV ? 
                                <>
                                    <DownloadButton loading={loading}  route={window.location.pathname} functionToExecute={()=>setShowDownloadOptions(!showDownloadOptions)} className="wd_100" btnName= {t("bulk_product_index.batch_download")} disableCondition={skeleton || loading || orderLevelData?.length == 0 || !orderLevelData}  endIconRequired={showDownloadOptions ? <ArrowDropUpIcon /> : <ArrowDropDownIcon/>} />
                                    {showDownloadOptions ?
                                        <><Box sx={{position:"relative"}}>
                                            <Button
                                                variant="contained"
                                                fullWidth
                                                sx={{marginTop:"1px",textTransform: "capitalize"}}
                                                className="product_list_Download_button margin_excel_download_product"
                                                disabled={skeleton ||  (totalCount <= BUSINESS_REPORT.BUSINESS_REPORT_DOWNLOAD_LIMIT_EXCEL ? false : true) }
                                                onClick={()=>downloadOrderLevelData(startDate,endDate,"xlsx")}
                                            >
                                                <Box component={"span"} sx={{fontSize:16}}> 
                                                    {t("product_list.excel_download")} 
                                                    <Box component={"div"} sx={{fontSize:10}}>
                                                        {t("product_list.excel_download_error")} 
                                                    </Box>
                                                </Box>
                                            </Button>
                                            <Button
                                                variant="contained"
                                                fullWidth
                                                sx={{ marginTop:"1px", textTransform: "capitalize"}}
                                                className="product_list_Download_button margin_csv_download_product"
                                                disabled={skeleton}
                                                onClick={()=>downloadOrderLevelData(startDate,endDate,"csv")}
                                            >
                                                <Box component={"span"} sx={{fontSize:16}}>
                                                    {t("product_list.csv_download")}
                                                    <Box component={"div"} sx={{fontSize:10}}>
                                                        {t("product_list.csv_download_error")}
                                                    </Box>
                                                </Box>                                                                                   
                                            </Button>
                                        </Box>
                                        </>
                                        : null
                                    }                              
                                </> 
                                :    
                                <>
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        endIcon={<ArrowDropDownIcon />}
                                        disabled={true}
                                    >
                                        {t("product_list.batch_download")}                                          
                                    </Button>
                                    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "center", mt: 1 }}>
                                        <CancelOutlinedIcon className="cross_icon" />
                                        <Typography variant="body2" sx={{ textAlign: "left", lineHeight: 1.6 }}>
                                            {t("product_list.download_limit_exceed")}
                                        </Typography>
                                    </Box>
                                </>
                        }
                    </Box>
                </Container>
                        
                {skeleton ? (
                    <Skeleton
                        varient="rectangular"
                        width={0}
                        height={60}
                        count="10"
                        my={0}
                    />
                ) : (
                    <MUIDataTable 
                        title={""}
                        columns={columns}
                        data={apiData}
                        options={{
                            pagination: false,
                            print: false,
                            download: false,
                            filter: false,
                            search: false,
                            selectableRowsHideCheckboxes: true,
                            textLabels: {
                                body: {
                                    noMatch: t("table_text.no_match").toString(),
                                },
                                viewColumns: {
                                    title: t("table_text.show_columns").toString(),
                                    titleAria: "Show/Hide Table Columns",
                                },
                                toolbar: {
                                    search: t("table_text.search").toString(),
                                    viewColumns: t("table_text.view_columns").toString(),
                                },
                            },
                            searchPlaceholder: t("table_text.search_placeholder").toString(),
                            responsive: "standard", 
                            onViewColumnsChange: (changedColumn:string,action:string) => {
                                const columnNameToFilterKey: { [key: string]: string } = {
                                    [t("pr.date")]: "date",
                                    [t("business_report.number_of_orders")]: "number_of_orders",
                                    [t("business_report.order_sales_amount")]: "order_sales_amount",
                                    [t("business_report.average_order_value")]: "average_order_value",
                                    [t("business_report.number_of_item_sold")]: "number_of_item_sold",
                                    [t("business_report.avg_no_item_sold")]: "avg_no_item_sold",
                                };

                                const tempHideColArr=[...hideColumnArr];
                                if(action==="remove"){
                                    changedColumn=="Date"?
                                        tempHideColArr.push("日付"):
                                        changedColumn=="Number of orders"?
                                            tempHideColArr.push("受注数"):
                                            changedColumn=="Order sales amount"?
                                                tempHideColArr.push("受注額"):
                                                changedColumn=="Average order value"?
                                                    tempHideColArr.push("平均受注額"):
                                                    changedColumn=="Number of item sold"?
                                                        tempHideColArr.push("商品実売点数"):
                                                        changedColumn=="Average Number of Items Sold"?
                                                            tempHideColArr.push("平均商品実売点数"):
                                                            tempHideColArr.push(changedColumn);

                                    setHideColumnArr(tempHideColArr);

                                    // Remove the hidden column's sort entry from colFilter + direction
                                    const filterKey = columnNameToFilterKey[changedColumn];
                                    if (filterKey) {
                                        const sortIdx = colFilter.indexOf(filterKey);
                                        if (sortIdx !== -1) {
                                            setColFilter(colFilter.filter((_, i) => i !== sortIdx));
                                            setDirection(direction.filter((_, i) => i !== sortIdx));
                                        }
                                    }
                                }
                                else{
                                    let index=-1;
                                    changedColumn=="Date"?
                                        index = tempHideColArr.indexOf("日付"):
                                        changedColumn=="Number of orders"?
                                            index = tempHideColArr.indexOf("受注数"):
                                            changedColumn=="Order sales amount"?
                                                index = tempHideColArr.indexOf("受注額"):
                                                changedColumn=="Average order value"?
                                                    index = tempHideColArr.indexOf("平均受注額"):
                                                    changedColumn=="Number of item sold"?
                                                        index = tempHideColArr.indexOf("商品実売点数"):
                                                        changedColumn=="Average Number of Items Sold"?
                                                            index = tempHideColArr.indexOf("平均商品実売点数"):
                                                            index = tempHideColArr.indexOf(changedColumn);
                                    tempHideColArr.splice(index,1);
                                    setHideColumnArr(tempHideColArr);
                                }
                            }                            
                        }}                       
                    />
                )} 
               
                <Pagination
                    totalCount={totalCount}
                    currentPage={currentPage}
                    pageChange={orderLevelDataApiCall}
                    totalPage={totalPages}
                    rowsPerPage={limit}
                    rowsPerPageChange={setLimit}
                />
            </Box>
        </>
    );
};

export default OrderReport;
